const article = {
	slug: "imposter-syndrome-identity-gap",
	title: "Why Brilliant Engineers Still Feel Like Frauds",
	date: "June 2025",
	updated: null,
	description: "Imposter syndrome is less about a lack of achievement and more about the gap between success and self-worth.",
	keywords: ["imposter syndrome", "psychological safety", "leadership", "engineering"],
	tldr: "Imposter syndrome is not about a lack of achievement. It is about a lack of integration between success and self-worth, and that makes it a leadership challenge, not just a personal one.",
	readingTime: null,
	provenance: "Adapted from my LinkedIn post, June 2025.",
	provenanceUrl: null,
	series: null,
	artifact: null,
	markdown: `Have you caught yourself looking in the mirror when you fail?

## A study about mirrors

This past month I overheard one of my wife's lectures while she was teaching from home. I got curious about a paper by Jeff Greenberg that she discussed with her students. This 1985 study explored how depressed individuals responded to success and failure.

TL;DR: When your internal narrative is self-critical, failure feels familiar. Success disrupts it.

Interestingly, depressed folks didn't avoid mirrors after failure, rather they preferred them. Success, on the other hand, felt alien. Almost unsettling.

The paper suggests that people with depressive tendencies often maintain large perceived self-ideal discrepancies and focus intensely on these gaps after experiencing failure.

It reminded me of a recent conversation with a seasoned engineer I respect at work. She shared how she supports her daughter when fear or failure makes her freeze up. Instead of rushing to solve it, she encourages her daughter to stay with the discomfort, talk to her thoughts, and journal them. By encouraging her to name her emotions, she's helping her daughter build awareness and healthier coping patterns.

## The parallel to my craft

These insights led me to consider some loose parallels with my craft.

In software engineering, we often see this dynamic play out as imposter syndrome.

Talented individuals ship complex features, lead migrations, unblock teams, and still walk away thinking they "got lucky." Even after years of impact, we wrestle with doubt.

Why do some of the most brilliant software engineers still feel like frauds? Maybe because imposter syndrome isn't about lack of achievement; it's about lack of integration between success and self-worth.

After talking to many seasoned as well as new engineers, I was surprised to learn that many of us never journal the wins. We downplay praise.

## Capturing the wins

That insight stuck with me. So I started wondering: what if we could collect those fleeting moments of praise, kindness, or affirmation and have it in engineers' workflow?

I tried building a Chrome extension to capture these micro-moments of encouragement, [brag-prompt-gdrive](https://github.com/swapniltamse/brag-prompt-gdrive).

In phase 2, I plan to build a weekly AI-generated summary from the Google docs, so you can reflect on your growth in your own words, and maybe start rewriting that inner narrative.

## A leadership challenge, not just a personal one

While serving on the Mergers & Acquisitions Inclusion Committee, I learned something that surprised me, thanks to Alex Ruiz's thoughtful leadership.

The biggest drivers of attrition among newly acquired engineers isn't compensation or technical misalignment. It was a lack of agency and ownership, and a perception that their past experience isn't valued in the new organization.

Imposter syndrome and lack of agency isn't just an individual challenge. It's a technical leadership challenge that affects talent retention, innovation, and team health.

*"Our deepest fear is not that we are inadequate. Our deepest fear is that we are powerful beyond measure."*

Marianne Williamson`,
};

export default article;
